import React, { useEffect, useState } from "react";
import { Calendar, ChevronRight, BookOpen } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await fetch(`${SERVER_URL}/api/blogs`);
        if (!response.ok) throw new Error("Failed to fetch blog posts");
        const data = await response.json();
        setPosts(data);
      } catch (error) {
        console.error("Error fetching posts:", error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const formatDateWithSuffix = (dateStr) => {
    const dateObj = new Date(dateStr);
    const day = dateObj.getDate();
    const month = dateObj.toLocaleString("default", { month: "long" });
    const year = dateObj.getFullYear();
    const getSuffix = (d) => {
      if (d > 3 && d < 21) return "th";
      switch (d % 10) {
        case 1:
          return "st";
        case 2:
          return "nd";
        case 3:
          return "rd";
        default:
          return "th";
      }
    };
    return `${day}${getSuffix(day)} ${month} ${year}`;
  };

  // Limit to 9 if showAll is false
  const displayedPosts = showAll ? posts : posts.slice(0, 9);

  return (
    <div className="min-h-screen bg-white pt-20 mt-12 md:mt-4 font-sans">
      <Helmet>
        <title>Fashion Blog | Pak Fashions</title>
        <meta
          name="description"
          content="Explore the latest in fashion trends, styling tips, and behind-the-scenes stories at Pak Fashions."
        />
        <meta
          name="keywords"
          content="fashion blog, style tips, clothing trends, Pak Fashions blog, Nanyuki, Laikipia, sustainable fashion"
        />
        <meta name="author" content="Pak Fashions" />
        <meta name="robots" content="index, follow" />

        {/* Open Graph */}
        <meta property="og:title" content="Fashion Blog | Pak Fashions" />
        <meta
          property="og:description"
          content="Stories, insights, and styling inspiration from the world of fashion."
        />
        <meta
          property="og:image"
          content="https://pakfashions.co.ke/PakFashionslogo.jpg"
        />
        <meta property="og:url" content="https://pakfashions.co.ke/blog" />
        <meta property="og:type" content="article" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Fashion Blog | Pak Fashions" />
        <meta
          name="twitter:description"
          content="Explore the latest fashion tips and trends from Pak Fashions."
        />
        <meta
          name="twitter:image"
          content="https://pakfashions.co.ke/PakFashionslogo.jpg"
        />

        {/* Optional Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            name: "Pak Fashions Blog",
            description:
              "Stories, insights, and styling inspiration from the world of fashion.",
            url: "https://pakfashions.co.ke/blog",
            publisher: {
              "@type": "Organization",
              name: "Pak Fashions",
              logo: {
                "@type": "ImageObject",
                url: "https://pakfashions.co.ke/pak-fashion.jpg",
              },
            },
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-white pt-24 pb-32 px-6 rounded-b-[4rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden mb-16 border-b border-gray-100 max-w-7xl mx-auto mt-[-4rem]">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-yellow-100 rounded-full blur-[120px] opacity-40 pointer-events-none transform translate-x-1/3 -translate-y-1/3"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-yellow-100 rounded-full blur-[120px] opacity-40 pointer-events-none transform -translate-x-1/3 translate-y-1/3"></div>
        
        <div className="relative z-10 text-center max-w-4xl mx-auto" data-aos="fade-down">
          <h1 className="text-5xl md:text-7xl font-black tracking-tight mb-8 text-gray-900 leading-[1.1]">
            Style <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-600">Journal</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-500 max-w-2xl mx-auto leading-relaxed font-medium">
            Stories, insights, and styling inspiration delivered fresh from the vibrant world of fashion.
          </p>
          <div className="h-1.5 w-24 bg-yellow-400 mx-auto mt-12 rounded-full shadow-sm" />
        </div>
      </section>

      {/* Loading State */}
      {loading ? (
        <div className="flex flex-col justify-center items-center py-32 opacity-70">
          <CircularProgress style={{ color: "#eab308" }} size={50} thickness={4} />
          <p className="mt-6 text-gray-500 font-medium tracking-wide">Curating stories...</p>
        </div>
      ) : (
        <section className="max-w-7xl mx-auto px-6 pb-24">
          {posts.length > 0 ? (
            <div className="grid gap-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {displayedPosts.map((post) => (
                <article
                  key={post._id}
                  className="group bg-white border border-gray-100 rounded-3xl p-8 hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all duration-500 relative flex flex-col h-full hover:-translate-y-1 cursor-pointer"
                  onClick={() => navigate(`/blog/${post._id}`)}
                >
                  <div className="absolute top-8 right-8 w-10 h-10 bg-yellow-50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transform group-hover:translate-x-0 translate-x-2 transition-all duration-300">
                    <ChevronRight className="w-5 h-5 text-yellow-600" />
                  </div>
                  
                  <div className="mb-6 flex items-center text-sm font-medium text-gray-400 bg-gray-50 w-fit px-3 py-1.5 rounded-full">
                    <Calendar className="w-4 h-4 mr-2 text-yellow-500" />
                    {formatDateWithSuffix(post.date)}
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-yellow-600 transition-colors line-clamp-2 leading-snug">
                    {post.title}
                  </h3>
                  
                  <p className="text-sm text-gray-500 font-medium mb-4 flex items-center">
                    <span className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center mr-2 text-xs">P</span>
                    {post.author || "Pak Fashions"}
                  </p>
                  
                  <p className="text-gray-600 mb-8 leading-relaxed line-clamp-3 flex-grow">
                    {post.description}
                  </p>
                  
                  <div className="mt-auto pt-6 border-t border-gray-100 border-dashed">
                    <span className="inline-flex items-center text-gray-900 font-bold group-hover:text-yellow-600 transition-colors">
                      <BookOpen className="w-4 h-4 mr-2 opacity-50 group-hover:opacity-100" />
                      Read Article
                    </span>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="text-center py-20 bg-gray-50 rounded-3xl border border-gray-100">
              <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">No Stories Yet</h3>
              <p className="text-gray-500 mb-8 font-medium">We're currently writing new fashion stories. Check back soon!</p>
            </div>
          )}

          {/* Show More / Show Less Button */}
          {posts.length > 9 && (
            <div className="text-center mt-16 pb-10">
              <button
                onClick={() => setShowAll(!showAll)}
                className="bg-gray-900 hover:bg-black text-white font-bold py-4 px-10 rounded-full shadow-lg shadow-gray-200 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
              >
                {showAll ? "Show Less Articles" : "Load More Articles"}
              </button>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

export default Blog;
